import type { FileParent } from "@msuite/citadel";
import {
	FileExplorer,
	type FileExplorerMenuExtension,
	type FileExplorerSelectColumn,
	type FileExplorerSelectOption,
} from "@msuite/ui/files";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { downloadZip } from "@msuite/utils/files";
import {
	documentTypes,
	type GetDocumentsQueryParams,
	type PatchDocumentBody,
} from "@msuite/utils/schemas";
import type {
	DocumentFilesTable,
	DocumentStructure,
	DocumentStructureFileItem,
	Enums,
	Tables,
	UploadResult,
} from "@msuite/utils/types";
import { deleteFileBatch, moveFileBatch, useUpload } from "@/api/files";
import { client } from "@/hono";
import { useFileCapabilities } from "@/hooks/use-file-capabilities";
import { useRemoteFileUrl } from "@/hooks/use-remote-file-url";

/**
 * A document as every collection serves it: the junction row with its file embedded. `type` and
 * `acknowledgementIntervalMonths` are the two columns only the classifying collections carry —
 * project and worksite documents — so both are optional here and the explorer offers them as
 * columns only where a patch route exists to write them.
 */
interface DocumentRow {
	id: string;
	file: Tables<"files">;
	type?: Enums<"documentType">;
	acknowledgementIntervalMonths?: number;
}

/** The intervals a risk assessment is asked for again after, as months. */
const ACKNOWLEDGEMENT_INTERVAL_MONTHS = [1, 3, 6, 12, 24] as const;

/** Props Interface */
interface DocumentsProps {
	documentFilesTable: DocumentFilesTable;
	identifier: string;
	/**
	 * The record `identifier` names, in the shape its rule binds on: the project, vehicle, protocol
	 * or calculation row, or the organisation itself for the worksite documents. Uploading, moving,
	 * reclassifying and removing all follow its `update`, so it is what the explorer's controls are
	 * asked of; `undefined` while the record is still loading, which keeps them.
	 */
	parent: FileParent | undefined;
	query?: GetDocumentsQueryParams;
	menu?: FileExplorerMenuExtension;
	/**
	 * The classifications this explorer offers per row. Left out where the documents are not
	 * reclassified here: the worksite explorer opens on risk assessments alone, so the only choice
	 * it could offer is the one every row already carries.
	 */
	typeOptions?: FileExplorerSelectOption[];
	/**
	 * Sends one document's change to the collection's PATCH route, which only the two classifying
	 * collections have — an explorer that passes this is therefore one of those two, and it is what
	 * the classification columns are rendered on. Without it every row is read-only.
	 */
	onPatchDocument?: (documentId: string, json: PatchDocumentBody) => void | Promise<void>;
	/**
	 * Takes every write control away whatever the parent's rule says — a locked calculation takes
	 * no files from anyone, and the server refuses them with 409.
	 */
	isReadOnly?: boolean;
}

export function Documents({
	documentFilesTable,
	identifier,
	parent,
	query,
	menu,
	typeOptions,
	onPatchDocument,
	isReadOnly = false,
}: DocumentsProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { uploadFn } = useUpload();
	const qc = useQueryClient();
	const files = useFileCapabilities(parent);

	/** Query */
	const {
		data: structure,
		refetch,
		isFetching,
		queryKey,
	} = useQuery({
		queryKey: [
			"documents",
			documentFilesTable,
			identifier,
			query?.additionalFields ? JSON.stringify(query.additionalFields) : "",
		],
		queryFn: async () => {
			const response = await client.documents[":documentFilesTable"][":identifier"].$get({
				param: { documentFilesTable, identifier },
				query: { additionalFields: query?.additionalFields },
			});
			const data = await responseHandler(response);
			return data.structure as DocumentStructure<DocumentRow>;
		},
		refetchInterval: Number.POSITIVE_INFINITY, // Disable auto refetching
		staleTime: 1000 * 60 * 5, // 5 minutes
	});

	/** Functions */
	async function handleOnInsertFilesCompleted(uploadResults: UploadResult[]) {
		if (uploadResults.length === 0) return;
		await client.documents[":documentFilesTable"][":identifier"].$post({
			param: { documentFilesTable, identifier },
			json: { uploadResults },
			query: { additionalFields: query?.additionalFields },
		});
	}

	async function handleOnDeleteFiles(fileIds: string[]) {
		if (fileIds.length === 0) return;
		await deleteFileBatch(fileIds);
	}

	async function handleOnDownloadFiles(fileIds: string[]) {
		if (fileIds.length === 0) return;
		const response = await client.files.batch.$get({
			query: { fileIds },
		});
		const data = await responseHandler(response);
		const urlItems = data.urlItems;
		if (!urlItems) throw new Error("No URLs returned for file download.");
		await downloadZip(urlItems, "project-files.zip");
	}

	/**
	 * Write one document's change, showing it in the table before the server has answered so the
	 * select box does not snap back to its old value for the length of the round trip. The refetch
	 * afterwards replaces the guess with what was actually stored.
	 */
	async function handleOnPatchDocument(
		item: DocumentStructureFileItem<DocumentRow>,
		json: PatchDocumentBody,
	) {
		qc.setQueryData<DocumentStructure<DocumentRow>>(queryKey, (old) => {
			if (!old) return old;
			return { ...old, items: patchStructureItems(old.items, item.file.id, json) };
		});
		await onPatchDocument?.(item.item.id, json);
		await refetch();
	}

	async function handleOnFolderDrop(newPath: string, fileIds: string[]) {
		await moveFileBatch(fileIds, newPath);
	}

	/** Computed */
	const selectColumns: FileExplorerSelectColumn<DocumentRow>[] = [];

	if (onPatchDocument && typeOptions?.length) {
		selectColumns.push({
			key: "type",
			header: t("general.type"),
			options: typeOptions,
			getValue: (item) => item.item.type ?? "default",
			onChange: async (item, value) => {
				const type = documentTypes.find((documentType) => documentType === value);
				if (!type) return;
				await handleOnPatchDocument(item, { type });
			},
		});
	}

	if (onPatchDocument) {
		selectColumns.push({
			key: "acknowledgementIntervalMonths",
			header: t("documents.acknowledgement_interval"),
			options: ACKNOWLEDGEMENT_INTERVAL_MONTHS.map((months) => ({
				value: String(months),
				label: t(`documents.acknowledgement_interval_months_${months}`),
			})),
			getValue: (item) => String(item.item.acknowledgementIntervalMonths ?? ""),
			onChange: async (item, value) => {
				await handleOnPatchDocument(item, { acknowledgementIntervalMonths: Number(value) });
			},
			// The interval is read for risk assessments alone, so anything else shows an empty cell
			// rather than a box whose value nothing would ever ask for.
			isEditable: (item) => item.item.type === "risk-assessment",
		});
	}

	/** Render */
	return (
		<FileExplorer
			structure={structure}
			uploadFn={uploadFn}
			handleOnInsertFilesCompleted={handleOnInsertFilesCompleted}
			refetch={refetch}
			isLoading={isFetching}
			useRemoteFileUrl={useRemoteFileUrl}
			onDeleteFiles={handleOnDeleteFiles}
			onDownloadFiles={handleOnDownloadFiles}
			handleOnFolderDrop={handleOnFolderDrop}
			menu={menu}
			selectColumns={selectColumns}
			canUpload={files.canWrite && !isReadOnly}
			canUpdateFile={(file) => !isReadOnly && files.canWriteFile(file)}
			canDeleteFile={(file) => !isReadOnly && files.canWriteFile(file)}
		/>
	);
}

/**
 * The tree with one document's patched fields written into it, rebuilt down to the row rather than
 * mutated so the query cache sees a new object and re-renders.
 *
 * @param items - The level of the tree being rewritten.
 * @param fileId - The file whose document row the patch belongs to.
 * @param json - The fields the patch names, copied over the row as they are.
 * @returns The same level, with the one row replaced.
 */
function patchStructureItems(
	items: DocumentStructure<DocumentRow>["items"],
	fileId: string,
	json: PatchDocumentBody,
): DocumentStructure<DocumentRow>["items"] {
	return items.map((item) => {
		if (item.type === "folder") {
			return { ...item, items: patchStructureItems(item.items, fileId, json) };
		}
		if (item.file.id !== fileId) return item;
		return { ...item, item: { ...item.item, ...json } };
	});
}
